// src/services/pushNotifications.js
// Faz 1C Görev 7 — FCM push notifications (client-side).
//
// Sorumluluk:
//   1) Browser support + permission kontrolü
//   2) FCM registration token al ve V3 authority store `userPushTokens/{token}`'a
//      yaz (PR-11b tek-yazıcı; legacy users/{uid}/fcmTokens'a yeni yazım YOK,
//      logout-delete köprüsü 11a gereği sürer)
//   3) Foreground mesajları `onMessage` ile dinle + deep-link için router'a bağla
//   4) Logout/unsubscribe: token'ı delete + server'a FirestoreClear
//
// Dedup: Doc ID = token string (replace(/[^a-zA-Z0-9_-]/g,"_")). Aynı token
// birden fazla yazılsa da tek doc olarak kalır (setDoc overwrite).
//
// Fire-and-forget: init hataları main UI'yi bozmaz, console warn.

import { auth, db, getMessagingInstance } from "../firebase";
import { doc, serverTimestamp, setDoc, deleteDoc } from "firebase/firestore";
import { getToken, onMessage, deleteToken } from "firebase/messaging";
import type { Auth } from "firebase/auth";
import { navigateToNotification } from "./notifications";
import { isIosSafariTab, computePushSupported } from "./pushPlatform";

// ─── yardımcılar ──────────────────────────────────────────────────────────
function logWarn(label: string, err: unknown): void {
  try {
    // `catch` değişkenleri `strict` altında `unknown`; log yalnız `message`
    // okuyup yoksa değerin kendisini basıyor.
    const hata = err as { message?: string } | null | undefined;
    // eslint-disable-next-line no-console
    console.warn(`[push] ${label}:`, hata?.message || err);
  } catch {}
}

// `info` gövdede OKUNMUYOR (log satırı kapalı) — imza korunuyor.
function logInfo(label: string, info: unknown): void {
  try {
    // eslint-disable-next-line no-console
  } catch {}
}

// Firestore doc id safe karakter
function safeTokenDocId(token: string | null | undefined): string {
  return String(token || "").replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 500);
}

function currentUid() {
  return auth?.currentUser?.uid || null;
}

function getVapidKey() {
  // CRA: runtime env var REACT_APP_* — build time'da inject edilir.
  const k = (process.env.REACT_APP_FIREBASE_VAPID_KEY || "").trim();
  return k || null;
}

// SW path — public/firebase-messaging-sw.js
const MESSAGING_SW_PATH = "/firebase-messaging-sw.js";

// Tek SW registration'ı paylaş
let _swRegPromise: Promise<ServiceWorkerRegistration | null> | null = null;
async function getMessagingSwRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (_swRegPromise) return _swRegPromise;
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }
  _swRegPromise = (async () => {
    try {
      // Daha önce register edilmiş mi kontrol et
      const existing = await navigator.serviceWorker.getRegistration(
        MESSAGING_SW_PATH
      );
      if (existing) return existing;
      const reg = await navigator.serviceWorker.register(MESSAGING_SW_PATH, {
        scope: "/firebase-cloud-messaging-push-scope",
      });
      return reg;
    } catch (e) {
      logWarn("registerSW", e);
      return null;
    }
  })();
  return _swRegPromise;
}

// ─── PUBLIC API ───────────────────────────────────────────────────────────

/* iOS web-push gating (iOS Safari TAB'ında push teslim edilmez) → saf mantık
   ./pushPlatform içinde (Firebase'siz, test edilebilir). isIosSafariTab
   geriye-uyumluluk için re-export edilir. */
export { isIosSafariTab };

export function isPushSupported() {
  try {
    return computePushSupported({
      isIosTab: isIosSafariTab(),
      hasNotification: typeof window !== "undefined" && "Notification" in window,
      hasServiceWorker: typeof navigator !== "undefined" && "serviceWorker" in navigator,
      hasPushManager: typeof PushManager !== "undefined",
    });
  } catch {
    return false;
  }
}

export function getPushPermissionState() {
  try {
    if (typeof Notification === "undefined") return "unsupported";
    return Notification.permission || "default"; // "granted" | "denied" | "default"
  } catch {
    return "unsupported";
  }
}

/**
 * Push bildirimlerini başlat. Permission ister, FCM token alır, Firestore'a yazar,
 * ve foreground mesajları dinlemeye başlar. Tekrar çağrı güvenli (idempotent).
 *
 * @param {object} opts
 * @param {boolean} [opts.requestPermission=true] — false ise sadece mevcut izinle dener
 * @returns {Promise<{ok:boolean, token?:string, reason?:string}>}
 */
export async function initPushNotifications({ requestPermission = true } = {}) {
  if (!isPushSupported()) {
    return { ok: false, reason: "unsupported" };
  }
  const uid = currentUid();
  if (!uid) return { ok: false, reason: "no_auth" };

  const vapid = getVapidKey();
  if (!vapid) {
    logWarn("init", "REACT_APP_FIREBASE_VAPID_KEY yok — push atlandı");
    return { ok: false, reason: "no_vapid" };
  }

  const messaging = await getMessagingInstance();
  if (!messaging) return { ok: false, reason: "messaging_unsupported" };

  // Permission
  let perm = getPushPermissionState();
  if (perm === "default" && requestPermission) {
    try {
      perm = await Notification.requestPermission();
    } catch (e) {
      logWarn("requestPermission", e);
      return { ok: false, reason: "perm_error" };
    }
  }
  if (perm !== "granted") return { ok: false, reason: `perm_${perm}` };

  // SW registration (messaging için dedicated SW)
  const swReg = await getMessagingSwRegistration();
  if (!swReg) return { ok: false, reason: "sw_unavailable" };

  // Token al
  let token = null;
  try {
    token = await getToken(messaging, {
      vapidKey: vapid,
      serviceWorkerRegistration: swReg,
    });
  } catch (e) {
    logWarn("getToken", e);
    return { ok: false, reason: "token_error" };
  }
  if (!token) return { ok: false, reason: "no_token" };

  // PR-11b — TEK YAZICI: yeni kayıtlar yalnız V3 authority store'a
  // (userPushTokens/{token}, docId = ham token; registerPushToken ile AYNI
  // şekil → rules create guard'ı geçer: uid==auth + token string ≤500).
  // Legacy users/{uid}/fcmTokens'a YENİ YAZIM DURDU — eski token'lar için
  // okuma köprüsü (onNotificationCreatePush) 11c'ye kadar aynen sürer;
  // logout cleanup (11a) iki store'u da silmeye devam eder.
  // NOT (paylaşılan cihaz): başka kullanıcının V3 doc'u client'tan
  // devralınamaz (rules update uid-guard) — devir 11a logout-delete'iyle
  // olur; A temiz çıkmadıysa B'nin kaydı token rotasyonuna kadar bekler
  // (fail-soft, app etkilenmez).
  try {
    await setDoc(
      doc(db, "userPushTokens", token),
      {
        uid,
        token,
        platform: "web",
        ua:
          typeof navigator !== "undefined" && navigator.userAgent
            ? String(navigator.userAgent).slice(0, 200)
            : "",
        lang:
          typeof navigator !== "undefined" ? navigator.language || "tr" : "tr",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    logWarn("saveToken", e);
    // Token'ı aldık ama yazamadık — yine de foreground handler kur
  }

  // Foreground handler — panel açıkken push gelirse banner yerine in-app UX.
  // Default davranış: console log + browser notification (SW göstermez).
  try {
    onMessage(messaging, (payload) => {
      try {
        logInfo("onMessage:foreground", payload);
        const n = payload?.notification || {};
        const data = payload?.data || {};
        // In-app banner yerine native Notification göster (tab aktif olsa da)
        if (getPushPermissionState() === "granted" && typeof Notification !== "undefined") {
          const notif = new Notification(n.title || "Yeni bildirim", {
            body: n.body || "",
            icon: n.icon || "/mylasa-192.png",
            badge: "/mylasa-192.png",
            data,
          });
          notif.onclick = () => {
            try {
              window.focus();
              if (data && data.link) {
                navigateToNotification({ link: data.link });
              }
              notif.close();
            } catch {}
          };
        }
      } catch (e) {
        logWarn("onMessage:handler", e);
      }
    });
  } catch (e) {
    logWarn("onMessage:register", e);
  }

  logInfo("ready", { token: token.slice(0, 16) + "…" });
  return { ok: true, token };
}

/**
 * Kullanıcı logout olunca veya token'ı yenilemek için kullan.
 * Token'ı hem Firestore'dan hem Messaging'den siler.
 */
export async function unregisterPushNotifications() {
  const uid = currentUid();
  if (!uid) return { ok: false, reason: "no_auth" };
  const messaging = await getMessagingInstance();
  if (!messaging) return { ok: false, reason: "messaging_unsupported" };

  try {
    // getToken ile mevcut token'ı al (yeni token yazmaz, cache'tekini döner)
    const vapid = getVapidKey();
    if (!vapid) return { ok: false, reason: "no_vapid" };
    const swReg = await getMessagingSwRegistration();
    if (!swReg) return { ok: false, reason: "sw_unavailable" };

    let token = null;
    try {
      token = await getToken(messaging, {
        vapidKey: vapid,
        serviceWorkerRegistration: swReg,
      });
    } catch {
      // ignore — belki yoktu
    }

    if (token) {
      try {
        await deleteDoc(doc(db, "users", uid, "fcmTokens", safeTokenDocId(token)));
      } catch (e) {
        logWarn("deleteTokenDoc", e);
      }
      // PR-11a — V3 deposu da temizlenir (userPushTokens/{token}, docId = ham
      // token; rules owner-delete'e izinli, rules:2404). Legacy silme başarısız
      // olsa bile bu BAĞIMSIZ denenir (paylaşılan-cihaz gizlilik açığı: çıkış
      // yapan kullanıcının DM push'ları cihaza düşmeye devam etmesin).
      try {
        await deleteDoc(doc(db, "userPushTokens", token));
      } catch (e) {
        logWarn("deleteV3TokenDoc", e);
      }
      try {
        await deleteToken(messaging);
      } catch (e) {
        logWarn("deleteToken", e);
      }
    }
    return { ok: true };
  } catch (e) {
    logWarn("unregister", e);
    return { ok: false };
  }
}

/**
 * PR-11a — Push-temizlikli çıkış. TÜM logout yolları bunu kullanır.
 *
 * Sıra kritik: token silme AUTH GEREKTİRİR (rules owner-delete) → cleanup
 * signOut'tan ÖNCE koşar. FAIL-SOFT + zaman sınırlı: cleanup hata verirse
 * veya 2.5sn'de bitmezse logout YİNE devam eder (kullanıcı asla çıkışta
 * kilitlenmez); token en geç login-tekrar akışında üzerine yazılır/ölür.
 */
export async function signOutWithPushCleanup(authInstance: Auth) {
  try {
    await Promise.race([
      unregisterPushNotifications(),
      new Promise((resolve) => setTimeout(resolve, 2500)),
    ]);
  } catch (e) {
    logWarn("signOutCleanup", e);
  }
  return authInstance.signOut();
}
